import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Trash2, Save, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const slugify = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

export default function SuperPages() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ slug: "", title: "", content: "", meta_description: "", show_in_footer: true });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pages");
    setItems(data);
    if (selected && !data.find((p) => p.slug === selected)) setSelected(null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    api.get(`/pages/${selected}`).then((r) => {
      if (cancelled) return;
      const p = r.data || {};
      setF({
        slug: p.slug || selected,
        title: p.title || "",
        content: p.content || "",
        meta_description: p.meta_description || "",
        show_in_footer: p.show_in_footer !== false,
      });
      setCreating(false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [selected]);

  const openNew = () => {
    setSelected(null);
    setCreating(true);
    setF({ slug: "", title: "", content: "", meta_description: "", show_in_footer: true });
  };

  const save = async () => {
    const payload = { ...f, slug: f.slug || slugify(f.title) };
    if (!payload.slug || !payload.title) return toast.error("Title and slug are required");
    try {
      await api.post("/pages", payload);
      toast.success("Page saved");
      setCreating(false);
      setSelected(payload.slug);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"?`)) return;
    try {
      await api.delete(`/pages/${slug}`);
      toast.success("Deleted");
      if (selected === slug) setSelected(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const modules = {
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "blockquote", "code-block"],
      ["clean"],
    ],
  };

  return (
    <DashLayout variant="super" title="Editorial Pages">
      <div className="grid md:grid-cols-4 gap-6">
        <Card className="p-4 md:col-span-1 h-fit">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900">All Pages</h3>
            <Button size="sm" variant="outline" onClick={openNew} data-testid="pages-new-btn">
              <Plus className="w-4 h-4 mr-1" />New
            </Button>
          </div>
          <div className="space-y-1">
            {items.length === 0 && <p className="text-xs text-slate-500 p-2">No pages yet.</p>}
            {items.map((p) => (
              <div
                key={p.slug}
                className={`flex items-center justify-between rounded-md px-2 py-1.5 cursor-pointer group ${
                  selected === p.slug ? "bg-blue-50 text-blue-700 border border-blue-200" : "hover:bg-slate-50 text-slate-700"
                }`}
                onClick={() => { setCreating(false); setSelected(p.slug); }}
                data-testid={`page-item-${p.slug}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-3.5 h-3.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.title}</div>
                    <div className="text-[10px] font-mono text-slate-400 truncate">/{p.slug}</div>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(p.slug); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`page-del-${p.slug}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 md:col-span-3 space-y-4">
          {!selected && !creating ? (
            <div className="text-center text-slate-500 py-16">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              Select a page on the left or click <span className="font-semibold">New</span> to create one.
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block text-xs">Title *</Label>
                  <Input
                    value={f.title}
                    onChange={(e) => setF((prev) => ({ ...prev, title: e.target.value, slug: creating && !prev.slug ? slugify(e.target.value) : prev.slug }))}
                    data-testid="page-title-input"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Slug (URL)</Label>
                  <Input
                    value={f.slug}
                    onChange={(e) => setF((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                    placeholder="privacy-policy"
                    disabled={!creating}
                    data-testid="page-slug-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Accessible at <code>/pages/{f.slug || "..."}</code></p>
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Meta Description (SEO)</Label>
                <Textarea rows={2} value={f.meta_description} onChange={(e) => setF((prev) => ({ ...prev, meta_description: e.target.value }))} data-testid="page-meta-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={f.show_in_footer} onCheckedChange={(v) => setF((prev) => ({ ...prev, show_in_footer: v }))} data-testid="page-footer-switch" />
                <Label className="text-sm">Show in footer</Label>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Content</Label>
                <div className="bg-white rounded border border-slate-200">
                  <ReactQuill
                    key={selected || (creating ? "new" : "empty")}
                    theme="snow"
                    value={f.content}
                    onChange={(v) => setF((prev) => ({ ...prev, content: v }))}
                    modules={modules}
                    style={{ minHeight: 300 }}
                    data-testid="page-content-editor"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} className="bg-blue-700 hover:bg-blue-800" data-testid="page-save-btn">
                  <Save className="w-4 h-4 mr-1" />Save Page
                </Button>
                {selected && (
                  <a
                    href={`/pages/${selected}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center px-3 py-2 border rounded text-sm hover:bg-slate-50"
                    data-testid="page-preview-btn"
                  >
                    Preview
                  </a>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </DashLayout>
  );
}
